// Implements the RFC 8032 Ed25519 signing algorithm using a raw signing scalar
// (not a seed) and a freshly generated nonce. Matches the reference
// implementation in the MPC-Ed25519 v1 spec appendix: the exported scalar goes
// in, a standard Ed25519 signature comes out. Verifiable by tweetnacl,
// crypto.subtle, or any other Ed25519 verifier.
import { ed25519 } from '@noble/curves/ed25519';
import { sha512 } from '@noble/hashes/sha512';
import bs58 from 'bs58';

const MAGIC = new TextEncoder().encode('MPCS');
const VERSION_V1 = 0x01;
const PAYLOAD_LEN = 4 + 1 + 32 + 32 + 4;

function crc32IEEE(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data[i];
    for (let j = 0; j < 8; j++) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

export type DecodedFormat = 'mpcs-v1' | 'raw-scalar';

export interface DecodedExport {
  scalar: Uint8Array;
  pubkey: Uint8Array;
  format: DecodedFormat;
}

export function decodeMPCSv1(encoded: string): DecodedExport {
  return decodeMPCSv1FromBytes(bs58.decode(encoded.trim()));
}

function decodeMPCSv1FromBytes(raw: Uint8Array): DecodedExport {
  if (raw.length !== PAYLOAD_LEN) {
    throw new Error(`expected ${PAYLOAD_LEN} bytes after base58 decode, got ${raw.length}`);
  }
  for (let i = 0; i < MAGIC.length; i++) {
    if (raw[i] !== MAGIC[i]) throw new Error('magic bytes do not match MPCS');
  }
  if (raw[4] !== VERSION_V1) {
    throw new Error(`unsupported version 0x${raw[4].toString(16)}`);
  }
  const crcOffset = PAYLOAD_LEN - 4;
  const expectedCRC =
    ((raw[crcOffset] << 24) >>> 0) |
    ((raw[crcOffset + 1] << 16) >>> 0) |
    ((raw[crcOffset + 2] << 8) >>> 0) |
    (raw[crcOffset + 3] >>> 0);
  if (crc32IEEE(raw.slice(0, crcOffset)) !== expectedCRC >>> 0) {
    throw new Error('CRC mismatch — the string is corrupted or not a v1 export');
  }
  return {
    scalar: raw.slice(5, 37),
    pubkey: raw.slice(37, 69),
    format: 'mpcs-v1',
  };
}

// Accepts the raw 32-byte FROST signing scalar in base58 (Para's "For
// scalarwallet.org" format — what `bs58.encode(scalar)` produces). The pubkey
// is derived as A = s·B. Not for RFC 8032 *seeds* — those need SHA-512 first
// and would derive a different pubkey.
export function decodeRawScalar(encoded: string): DecodedExport {
  return decodeRawScalarBytes(bs58.decode(encoded.trim()));
}

// 64-char hex (with or without `0x`) interpreted as a raw 32-byte scalar.
export function decodeRawScalarHex(encoded: string): DecodedExport {
  return decodeRawScalarBytes(hexToBytes(encoded.trim().replace(/^0x/i, '')));
}

function decodeRawScalarBytes(raw: Uint8Array): DecodedExport {
  if (raw.length !== 32) {
    throw new Error(`expected 32 bytes for a raw scalar, got ${raw.length}`);
  }
  const reduced = bytesToScalar(raw) % ed25519.CURVE.n;
  if (reduced === 0n) throw new Error('scalar reduces to zero');
  const scalar = scalarToBytes(reduced);
  const pubkey = ed25519.ExtendedPoint.BASE.multiply(reduced).toRawBytes();
  return { scalar, pubkey, format: 'raw-scalar' };
}

export function decodeKey(encoded: string): DecodedExport {
  const trimmed = encoded.trim();

  // 64-char hex (optionally `0x`-prefixed) is unambiguously hex — base58
  // alphabet excludes `0`, and a 64-char base58 string decodes to ~47 bytes,
  // not 32, so there is no collision.
  const stripped = trimmed.replace(/^0x/i, '');
  if (/^[0-9a-fA-F]{64}$/.test(stripped)) return decodeRawScalarHex(stripped);

  // Base58: route on decoded length, not on the encoded string. Base58 is a
  // base conversion, so the encoded form of an MPCS payload doesn't literally
  // start with "MPCS" — the magic bytes are inside the integer. The MPCS-v1
  // envelope decodes to 73 bytes; a raw FROST scalar decodes to 32.
  const raw = bs58.decode(trimmed);
  if (raw.length === PAYLOAD_LEN) return decodeMPCSv1FromBytes(raw);
  if (raw.length === 32) return decodeRawScalarBytes(raw);
  throw new Error(`expected 32 or ${PAYLOAD_LEN} bytes after base58 decode, got ${raw.length}`);
}

function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new Error('hex string must have even length');
  if (!/^[0-9a-fA-F]*$/.test(hex)) throw new Error('hex string contains non-hex characters');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function bytesToScalar(bytes: Uint8Array): bigint {
  let n = 0n;
  for (let i = bytes.length - 1; i >= 0; i--) {
    n = (n << 8n) | BigInt(bytes[i]);
  }
  return n;
}

function reduceUniform(bytes64: Uint8Array): bigint {
  let n = 0n;
  for (let i = bytes64.length - 1; i >= 0; i--) {
    n = (n << 8n) | BigInt(bytes64[i]);
  }
  return n % ed25519.CURVE.n;
}

function scalarToBytes(n: bigint): Uint8Array {
  const out = new Uint8Array(32);
  let x = n;
  for (let i = 0; i < 32; i++) {
    out[i] = Number(x & 0xffn);
    x >>= 8n;
  }
  return out;
}

// Signs `msg` using a raw FROST signing scalar `s`. Standard Ed25519 from
// s-as-signing-key onward: pick a fresh random nonce r, R = r·B, c = SHA-512(R
// || A || M) mod l, S = r + c·s mod l. Returns a 64-byte signature (R || S).
export function ed25519SignWithScalar(scalar: Uint8Array, pubkey: Uint8Array, msg: Uint8Array): Uint8Array {
  const s = bytesToScalar(scalar);

  const nonceSeed = new Uint8Array(64);
  crypto.getRandomValues(nonceSeed);
  const r = reduceUniform(nonceSeed);

  const R = ed25519.ExtendedPoint.BASE.multiply(r === 0n ? 1n : r);
  const RBytes = R.toRawBytes();

  const challengeInput = new Uint8Array(RBytes.length + pubkey.length + msg.length);
  challengeInput.set(RBytes, 0);
  challengeInput.set(pubkey, RBytes.length);
  challengeInput.set(msg, RBytes.length + pubkey.length);
  const c = reduceUniform(sha512(challengeInput));

  const S = (r + ((c * s) % ed25519.CURVE.n)) % ed25519.CURVE.n;

  const sig = new Uint8Array(64);
  sig.set(RBytes, 0);
  sig.set(scalarToBytes(S), 32);
  return sig;
}

export function bytesToHex(b: Uint8Array): string {
  return Array.from(b)
    .map(x => x.toString(16).padStart(2, '0'))
    .join('');
}
